import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { CalendarDays, MapPin, Clock, Users, Trophy, Globe, Wrench, Zap, Handshake, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eventTypeIcons = {
  'Hackathon': Wrench, 'Workshop': Zap, 'Pitch Night': Trophy,
  'Speaker Session': Users, 'Networking': Handshake
};

const eventTypeColors = {
  'Hackathon': 'from-purple-600 to-purple-800',
  'Workshop': 'from-blue-600 to-blue-800',
  'Pitch Night': 'from-[#C83041] to-[#8B1E2D]',
  'Speaker Session': 'from-green-600 to-green-800',
  'Networking': 'from-[#075FA3] to-[#04365E]'
};

const signatureEvents = [
  { icon: Trophy, title: "Zero-to-One Pitch Night", description: "Students pitch early-stage startup ideas to mentors and judges" },
  { icon: Globe, title: "Confluence", description: "Flagship event connecting founders, investors, and students" },
  { icon: Wrench, title: "Campus Problem Hackathon", description: "Building solutions to real campus problems" },
  { icon: Zap, title: '"Build with 3 Tools" Challenge', description: "Constraint-based startup building using only Notion, Canva, and Google Sheets" },
  { icon: Handshake, title: "Founder Matching Nights", description: "Connecting students across disciplines to form co-founding teams" }
];

// --- Countdown Logic ---
function getCountdown(targetISO) {
  if (!targetISO) return null;
  const now = Date.now();
  const target = new Date(targetISO).getTime();
  const diff = target - now;
  if (diff <= 0) return null;
  const d = Math.floor(diff / (1000 * 60 * 60 * 24));
  const h = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const m = Math.floor((diff / (1000 * 60)) % 60);
  const s = Math.floor((diff / 1000) % 60);
  return { d, h, m, s, total: diff };
}

function useCountdown(targetISO) {
  const [countdown, setCountdown] = useState(() => getCountdown(targetISO));
  useEffect(() => {
    if (!targetISO) return;
    const id = setInterval(() => setCountdown(getCountdown(targetISO)), 1000);
    return () => clearInterval(id);
  }, [targetISO]);
  return countdown;
}

// --- Countdown Display Components ---
function CountdownUnit({ value, label }) {
  return (
    <div className="flex flex-col items-center">
      <span className="text-2xl md:text-4xl font-bold tabular-nums font-['Bricolage_Grotesque']">{String(value).padStart(2, '0')}</span>
      <span className="text-[10px] md:text-xs uppercase tracking-wider opacity-70 mt-1">{label}</span>
    </div>
  );
}

function HeroCountdown({ targetISO }) {
  const cd = useCountdown(targetISO);
  if (!cd) return <p className="text-white/60 text-lg">Starting soon...</p>;
  return (
    <div className="flex gap-3 md:gap-6 text-white" data-testid="hero-countdown">
      {cd.d > 0 && <><CountdownUnit value={cd.d} label="Days" /><span className="text-3xl md:text-4xl font-light opacity-40 mt-1">:</span></>}
      <CountdownUnit value={cd.h} label="Hours" />
      <span className="text-3xl md:text-4xl font-light opacity-40 mt-1">:</span>
      <CountdownUnit value={cd.m} label="Min" />
      <span className="text-3xl md:text-4xl font-light opacity-40 mt-1">:</span>
      <CountdownUnit value={cd.s} label="Sec" />
    </div>
  );
}

function CardCountdown({ targetISO }) {
  const cd = useCountdown(targetISO);
  if (!cd) return <span className="text-xs text-gray-400">Starting soon</span>;
  const parts = [];
  if (cd.d > 0) parts.push(`${cd.d}d`);
  parts.push(`${cd.h}h`, `${cd.m}m`, `${cd.s}s`);
  return (
    <div className="flex items-center gap-1.5 font-mono text-sm font-semibold text-[#C83041]" data-testid="card-countdown">
      <Clock className="w-3.5 h-3.5" />
      {parts.join(' ')}
    </div>
  );
}

// --- Helpers ---
function getEventDateTime(event) {
  if (event.event_datetime) return event.event_datetime;
  if (event.date) return new Date(event.date + 'T00:00:00').toISOString();
  return null;
}

function isEventUpcoming(event) {
  const dt = getEventDateTime(event);
  if (!dt) return !event.is_past;
  return new Date(dt).getTime() > Date.now();
}

function formatEventDate(event) {
  const dt = getEventDateTime(event);
  if (!dt) return 'Date TBD';
  return new Date(dt).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

function formatEventTime(event) {
  if (event.event_datetime) {
    return new Date(event.event_datetime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  }
  if (event.time) return event.time;
  return 'Time TBD';
}

function getImageUrl(url) {
  if (!url) return '';
  if (url.startsWith('cfe-krea/')) return `${API}/files/${url}`;
  return url;
}

// --- Event Card ---
function EventCard({ event, onRegister }) {
  const IconComponent = eventTypeIcons[event.event_type] || CalendarDays;
  const gradientClass = eventTypeColors[event.event_type] || 'from-gray-600 to-gray-800';
  const upcoming = isEventUpcoming(event);
  const dt = getEventDateTime(event);

  return (
    <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 border-0 shadow-md" data-testid={`event-card-${event.id}`}>
      <CardContent className="p-0">
        {event.image_url ? (
          <div className="overflow-hidden relative bg-[#231F20]">
            <img src={getImageUrl(event.image_url)} alt={event.title} className="w-full h-auto object-contain" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-3 left-4 flex items-center gap-2">
              <Badge className="bg-white/90 text-[#231F20] text-xs">{event.event_type}</Badge>
              {event.is_virtual && <Badge className="bg-green-500 text-white text-xs">Virtual</Badge>}
            </div>
          </div>
        ) : (
          <div className={`h-32 bg-gradient-to-br ${gradientClass} flex items-center justify-between px-6 relative overflow-hidden`}>
            <div className="absolute -right-6 -top-6 w-28 h-28 rounded-full bg-white/5" />
            <div className="absolute -right-2 -bottom-8 w-20 h-20 rounded-full bg-white/5" />
            <div className="flex items-center gap-3 relative z-10">
              <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center">
                <IconComponent className="w-5 h-5 text-white" />
              </div>
              <div>
                <Badge className="bg-white/20 text-white text-xs border-0">{event.event_type}</Badge>
                {event.is_virtual && <Badge className="bg-green-400/30 text-green-100 text-xs border-0 ml-1">Virtual</Badge>}
              </div>
            </div>
            {upcoming && dt && <CardCountdown targetISO={dt} />}
          </div>
        )}

        <div className="p-5">
          <h3 className="text-lg font-bold text-[#231F20] mb-2 font-['Bricolage_Grotesque'] line-clamp-1">{event.title}</h3>
          <p className="text-[#58595B] text-sm mb-4">{event.description}</p>

          <div className="space-y-1.5 mb-5">
            <div className="flex items-center gap-2 text-sm text-[#58595B]">
              <CalendarDays className="w-4 h-4 text-[#075FA3] flex-shrink-0" />
              {formatEventDate(event)}
            </div>
            <div className="flex items-center gap-2 text-sm text-[#58595B]">
              <Clock className="w-4 h-4 text-[#075FA3] flex-shrink-0" />
              {formatEventTime(event)}
            </div>
            <div className="flex items-center gap-2 text-sm text-[#58595B]">
              <MapPin className="w-4 h-4 text-[#075FA3] flex-shrink-0" />
              {event.location}
            </div>
          </div>

          {upcoming ? (
            <div className="flex items-center justify-between">
              <Button onClick={() => onRegister(event)} className="flex-1 bg-[#C83041] hover:bg-[#A92332] text-white rounded-full" data-testid={`register-event-btn-${event.id}`}>
                Register Now
              </Button>
              {event.registration_count > 0 && (
                <span className="text-xs text-[#58595B] ml-3 flex-shrink-0">{event.registration_count} joined</span>
              )}
            </div>
          ) : (
            <Button variant="outline" className="w-full rounded-full border-gray-300 text-gray-400" disabled>
              Event Completed
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// --- Main Page ---
export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [registering, setRegistering] = useState(false);
  const emptyMember = { name: '', email: '', phone: '', affiliation: '', batch_year: '', is_primary: false };
  const [teamName, setTeamName] = useState('');
  const [members, setMembers] = useState([{ ...emptyMember, is_primary: true }]);
  const [, setTick] = useState(0);

  const fetchEvents = useCallback(async () => {
    try { const response = await axios.get(`${API}/events`); setEvents(response.data); }
    catch { /* silent */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // Re-sort every 60s to auto-move past events
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(id);
  }, []);

  const upcomingEvents = events
    .filter(isEventUpcoming)
    .sort((a, b) => new Date(getEventDateTime(a)) - new Date(getEventDateTime(b)));

  const pastEvents = events
    .filter(e => !isEventUpcoming(e))
    .sort((a, b) => new Date(getEventDateTime(b)) - new Date(getEventDateTime(a)));

  const heroEvent = upcomingEvents[0] || null;
  const heroIcon = heroEvent ? (eventTypeIcons[heroEvent.event_type] || CalendarDays) : CalendarDays;

  const handleRegister = (event) => {
    setSelectedEvent(event);
    setTeamName('');
    setMembers([{ ...emptyMember, is_primary: true }]);
    setIsRegisterModalOpen(true);
  };

  const updateMember = (index, field, value) => {
    setMembers(prev => prev.map((m, i) => i === index ? { ...m, [field]: value } : m));
  };

  const addMember = () => {
    setMembers(prev => [...prev, { ...emptyMember }]);
  };

  const removeMember = (index) => {
    setMembers(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmitRegistration = async (e) => {
    e.preventDefault();
    if (!selectedEvent) return;
    setRegistering(true);
    try {
      await axios.post(`${API}/events/${selectedEvent.id}/register`, {
        event_id: selectedEvent.id,
        team_name: teamName,
        members: members
      });
      toast.success('Registration successful!', { description: `Team "${teamName}" is registered for ${selectedEvent.title}` });
      setIsRegisterModalOpen(false);
      fetchEvents();
    } catch (error) {
      toast.error('Registration failed', { description: error.response?.data?.detail || 'Please try again' });
    } finally { setRegistering(false); }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section — Nearest Event or Fallback */}
      <section className="relative overflow-hidden" data-testid="events-hero">
        {heroEvent ? (
          <div className={`py-20 md:py-28 bg-gradient-to-br ${eventTypeColors[heroEvent.event_type] || 'from-[#075FA3] to-[#04365E]'} relative`}>
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-10 left-10 w-72 h-72 rounded-full bg-white blur-3xl" />
              <div className="absolute bottom-10 right-20 w-96 h-96 rounded-full bg-white blur-3xl" />
            </div>
            <div className="container-main relative z-10">
              <div className="max-w-3xl">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                    <heroIcon className="w-5 h-5 text-white" />
                  </div>
                  <Badge className="bg-white/20 text-white border-0 text-sm px-3 py-1">{heroEvent.event_type}</Badge>
                  <span className="text-white/60 text-sm">Up Next</span>
                </motion.div>
                <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                  className="text-4xl md:text-6xl font-bold text-white mb-4 font-['Bricolage_Grotesque']" data-testid="hero-event-title">
                  {heroEvent.title}
                </motion.h1>
                <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
                  className="text-lg text-white/80 mb-6 max-w-2xl">
                  {heroEvent.description}
                </motion.p>
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                  className="flex flex-wrap items-center gap-4 text-white/70 text-sm mb-8">
                  <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4" /> {formatEventDate(heroEvent)}</span>
                  <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {formatEventTime(heroEvent)}</span>
                  <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {heroEvent.location}</span>
                </motion.div>
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
                  className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 inline-block mb-8">
                  <p className="text-white/60 text-xs uppercase tracking-widest mb-3">Starts In</p>
                  <HeroCountdown targetISO={getEventDateTime(heroEvent)} />
                </motion.div>
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                  <Button onClick={() => handleRegister(heroEvent)}
                    className="bg-white text-[#231F20] hover:bg-gray-100 rounded-full px-8 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1"
                    data-testid="hero-register-btn">
                    Register for This Event
                  </Button>
                </motion.div>
              </div>
            </div>
          </div>
        ) : (
          <div className="py-24 md:py-32 bg-[#075FA3] relative">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-10 left-10 w-72 h-72 rounded-full bg-[#4DBDDE] blur-3xl" />
              <div className="absolute bottom-10 right-10 w-96 h-96 rounded-full bg-[#C83041] blur-3xl" />
            </div>
            <div className="container-main relative z-10">
              <div className="max-w-3xl">
                <motion.span initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                  className="text-[#4DBDDE] font-semibold text-sm tracking-wider uppercase mb-4 block">Happenings</motion.span>
                <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                  className="text-5xl md:text-6xl font-bold text-white mb-6 font-['Bricolage_Grotesque']" data-testid="events-heading">
                  CfE Events
                </motion.h1>
                <motion.p initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                  className="text-xl text-white/80 leading-relaxed">
                  From hackathons to pitch nights, workshops to networking sessions &mdash; discover events that will accelerate your entrepreneurial journey.
                </motion.p>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Signature Events */}
      <section className="py-16 bg-[#231F20]" data-testid="signature-events">
        <div className="container-main">
          <h3 className="text-2xl font-bold text-white mb-8 font-['Bricolage_Grotesque']">Our Signature Events</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {signatureEvents.map((event, index) => (
              <motion.div key={event.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.08 }}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 hover:bg-white/10 hover:border-[#4DBDDE]/50 transition-all duration-300 group">
                <div className="w-10 h-10 rounded-xl bg-[#C83041]/20 flex items-center justify-center mb-3 group-hover:bg-[#C83041] transition-colors">
                  <event.icon className="w-5 h-5 text-[#C83041] group-hover:text-white transition-colors" />
                </div>
                <h4 className="text-sm font-semibold text-white mb-1">{event.title}</h4>
                <p className="text-xs text-white/50">{event.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="section-padding bg-white" data-testid="upcoming-events">
        <div className="container-main">
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold text-[#231F20] mb-8 font-['Bricolage_Grotesque']">
            Upcoming Events
          </motion.h2>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map(i => <div key={i} className="animate-pulse"><div className="bg-gray-200 rounded-2xl h-80" /></div>)}
            </div>
          ) : upcomingEvents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {upcomingEvents.map((event, index) => (
                <motion.div key={event.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}>
                  <EventCard event={event} onRegister={handleRegister} />
                </motion.div>
              ))}
            </div>
          ) : (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative rounded-3xl overflow-hidden">
              <div className="bg-gradient-to-br from-[#075FA3] to-[#4DBDDE] p-16 text-center">
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-white blur-3xl" />
                  <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-[#C83041] blur-3xl" />
                </div>
                <div className="relative z-10">
                  <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-6">
                    <CalendarDays className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3 font-['Bricolage_Grotesque']">Events Coming Soon</h3>
                  <p className="text-white/80 max-w-md mx-auto text-lg">
                    We&apos;re planning exciting hackathons, pitch nights, workshops, and more. Stay tuned!
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </section>

      {/* Past Events */}
      {pastEvents.length > 0 && (
        <section className="section-padding bg-[#F3F4F6]" data-testid="past-events">
          <div className="container-main">
            <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold text-[#231F20] mb-8 font-['Bricolage_Grotesque']">
              Past Events
            </motion.h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {pastEvents.map((event, index) => (
                <motion.div key={event.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}>
                  <EventCard event={event} onRegister={handleRegister} />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Registration Modal */}
      <Dialog open={isRegisterModalOpen} onOpenChange={setIsRegisterModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#231F20] font-['Bricolage_Grotesque']">Register for Event</DialogTitle>
            <DialogDescription className="text-[#58595B]">{selectedEvent?.title}</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-[#58595B] bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
            Only one registration is required per team. Please add the details of all your team members below.
          </p>
          <form onSubmit={handleSubmitRegistration} className="space-y-5 mt-2">
            <div>
              <Label className="form-label">Team Name *</Label>
              <Input value={teamName} onChange={(e) => setTeamName(e.target.value)} required placeholder="Enter your team name" data-testid="reg-team-name" />
            </div>

            {members.map((member, idx) => (
              <div key={idx} className={`p-4 rounded-xl border ${idx === 0 ? 'border-[#075FA3]/30 bg-[#075FA3]/5' : 'border-gray-200 bg-gray-50'}`}>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-[#231F20]">
                    {idx === 0 ? 'Team Member 1 (Primary Contact)' : `Team Member ${idx + 1}`}
                  </h4>
                  {idx > 0 && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeMember(idx)} className="text-red-500 hover:text-red-700 h-7 px-2">
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
                    </Button>
                  )}
                </div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-[#58595B]">Full Name *</Label>
                    <Input value={member.name} onChange={(e) => updateMember(idx, 'name', e.target.value)} required placeholder="Full name" data-testid={`reg-name-${idx}`} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-[#58595B]">Email *</Label>
                      <Input type="email" value={member.email} onChange={(e) => updateMember(idx, 'email', e.target.value)} required placeholder="email@example.com" data-testid={`reg-email-${idx}`} />
                    </div>
                    <div>
                      <Label className="text-xs text-[#58595B]">{idx === 0 ? 'Phone *' : 'Phone'}</Label>
                      <Input type="tel" value={member.phone} onChange={(e) => updateMember(idx, 'phone', e.target.value)} placeholder="+91 XXXXX" {...(idx === 0 ? { required: true } : {})} data-testid={`reg-phone-${idx}`} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-[#58595B]">Krea Affiliation *</Label>
                      <Select value={member.affiliation} onValueChange={(v) => updateMember(idx, 'affiliation', v)}>
                        <SelectTrigger data-testid={`reg-affiliation-${idx}`}><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Current Student">Current Student</SelectItem>
                          <SelectItem value="Alumni">Alumni</SelectItem>
                          <SelectItem value="Faculty">Faculty</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-[#58595B]">Batch / Year</Label>
                      <Input value={member.batch_year} onChange={(e) => updateMember(idx, 'batch_year', e.target.value)} placeholder="e.g., 2024" data-testid={`reg-batch-${idx}`} />
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <Button type="button" variant="outline" onClick={addMember} className="w-full border-dashed border-2 text-[#075FA3] hover:bg-[#075FA3]/5" data-testid="add-member-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Team Member
            </Button>

            <Button type="submit"
              disabled={registering || !teamName || !members[0].name || !members[0].email || !members[0].affiliation}
              className="w-full bg-[#C83041] hover:bg-[#A92332] text-white rounded-full py-6 font-semibold" data-testid="submit-registration-btn">
              {registering ? 'Registering...' : `Register Team (${members.length} member${members.length > 1 ? 's' : ''})`}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
